import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  DEPARTMENT_LABEL,
  ROLES,
  ROLE_LABEL,
  accessGaps,
  canRevoke,
  shownColumns,
  isLockedPermission,
  isLockedView,
  type AccessMatrix,
  type Role,
  type User,
} from '@/domain';
import {
  useAccessMatrix,
  useRestoreUser,
  useRevokeUser,
  useSaveAccessMatrix,
  useUsers,
} from '@/application/usecases/useAdmin';
import { useSession } from '@/application/usecases/useSession';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { RoleNotificationSettings } from './NotificationSettings';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { CustomizeColumns, type TableCustomize } from '@/ui/data/CustomizeColumns';
import { initials } from '@/shared/format/initials';
import { formatDay } from '@/shared/format/instant';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { toast } from '@/ui/primitives/toastStore';
import { Tabs } from '@/ui/primitives/Tabs';
import { LockIcon, PlusIcon } from '@/ui/icons';
import { AccessGaps } from './AccessGaps';
import { AddUserModal, EditUserModal } from './AddUserModal';
import { SetPasswordModal } from './SetPasswordModal';
import { SkeletonRows } from '@/ui/feedback/Loading';
import styles from './UsersScreen.module.css';

/**
 * Users & permissions. Sections 5.2 and 4.2.
 *
 * Two halves. The team list creates, revokes and restores people; the access
 * matrix below it grants and revokes screens and actions per role, at runtime.
 *
 * Both of the rules that make this screen safe are enforced by the server and
 * mirrored here from the same domain functions, not reimplemented:
 * `canRevoke` for section 7.10 item 8, and `isLockedView` for item 7. The
 * mirror is so the button is disabled rather than offered and then refused;
 * the server refusing is what actually protects the system.
 */
export function UsersScreen() {
  const [adding, setAdding] = useState(false);
  const [settingPasswordFor, setSettingPasswordFor] = useState<User | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [revoking, setRevoking] = useState<User | null>(null);
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const users = useUsers();
  const matrix = useAccessMatrix();
  const revoke = useRevokeUser();
  const restore = useRestoreUser();
  // Restoring is one tap, so it is asked about first. The request of 5 October 2026 (third), section 2.
  const ask = useConfirmAction();

  const me = session.data?.user.id ?? '';
  const team = users.data?.items ?? [];

  /*
   * Four tabs, because the screen had grown into four unrelated screens
   * stacked on one page: a team list, a revoked list buried inside it, a
   * permission grid and a map. A manager restoring somebody's access and a
   * manager setting the lab radius are not doing the same job, and scrolling
   * past one to reach the other is not a way of separating them.
   *
   * The tab lives in the URL, so a manager can send a colleague a link that
   * opens on the matrix and the browser's back button steps between tabs the
   * way it looks as though it should.
   */
  const tab = TAB_KEYS.includes((search.get('tab') ?? '') as TabKey)
    ? (search.get('tab') as TabKey)
    : 'team';

  const active = team.filter((user) => !user.disabled);
  const revoked = team.filter((user) => user.disabled);
  const busy = revoke.isPending || restore.isPending;

  // The columns as this reader laid them out, the same on Team and on Revoked.
  // The request of 7 October 2026, section 3.
  const layout = useTableLayout('users');
  const shown = shownColumns(TEAM_COLUMNS, layout.layout);

  const rowFor = (user: User) => (
    <TeamRow
      key={user.id}
      shown={shown}
      user={user}
      isSelf={user.id === me}
      // The domain rule, not a repeat of it. Unknown until the session lands,
      // and an unknown caller may not revoke anyone.
      mayRevoke={me !== '' && canRevoke(me, user.id)}
      busy={busy}
      onOpen={() => void navigate(`/users/${user.id}`)}
      onSetPassword={() => setSettingPasswordFor(user)}
      onEdit={() => setEditing(user)}
      onRevoke={() => setRevoking(user)}
      onRestore={() =>
        ask(
          strings.confirm.restoreUser(user.name),
          () =>
            restore
              .mutateAsync(user.id)
              .then(() => toast(`${strings.users.restoredToast} ${user.name}`)),
          { title: strings.users.restore, confirmLabel: strings.users.restore },
        )
      }
    />
  );

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.users.label}</span>
        <h2>{strings.users.title}</h2>
        <span className={styles.hint}>{strings.users.hint}</span>
      </div>

      <Tabs
        label={strings.users.tabsLabel}
        current={tab}
        onSelect={(key) => setSearch(key === 'team' ? {} : { tab: key }, { replace: true })}
        tabs={[
          { key: 'team', label: strings.users.tabTeam, badge: active.length },
          { key: 'revoked', label: strings.users.tabRevoked, badge: revoked.length },
          { key: 'matrix', label: strings.users.tabMatrix },
          // Who is notified of what. The request of 7 October 2026, section 1.
          { key: 'notifications', label: strings.notifications.admin.tab },
        ]}
      >
        {users.isError && <p className={styles.hint}>{strings.errors.server}</p>}

        {tab === 'team' && (
          <>
            <div className={styles.section}>
              <h3>
                {active.length}{' '}
                {active.length === 1 ? strings.users.teamCountOne : strings.users.teamCount}
              </h3>
              <span className={styles.spacer} />
              <Button icon={<PlusIcon />} onClick={() => setAdding(true)}>
                {strings.users.add}
              </Button>
            </div>
            <p className={styles.hint}>{strings.users.teamHint}</p>

            {users.isPending ? (
              <SkeletonRows rows={5} label={strings.common.loadingUsers} />
            ) : (
              <TeamTable rows={active.map(rowFor)} shown={shown} customize={layout} />
            )}
          </>
        )}

        {tab === 'revoked' && (
          <>
            <div className={styles.section}>
              <h3>{strings.users.revokedTitle}</h3>
            </div>
            <p className={styles.hint}>{strings.users.revokedHint}</p>

            {users.isPending ? (
              <SkeletonRows rows={3} label={strings.common.loadingUsers} />
            ) : revoked.length === 0 ? (
              <div className={styles.empty}>
                <h4>{strings.users.revokedEmpty}</h4>
                <p>{strings.users.revokedEmptyDetail}</p>
              </div>
            ) : (
              <TeamTable rows={revoked.map(rowFor)} shown={shown} customize={layout} />
            )}
          </>
        )}

        {tab === 'matrix' && (
          <>
            <div className={styles.section}>
              <h3>{strings.users.matrixTitle}</h3>
            </div>
            <p className={styles.hint}>{strings.users.matrixHint}</p>
            <p className={styles.hint}>{strings.users.matrixExceptions}</p>

            {matrix.isPending && <SkeletonRows rows={4} label={strings.common.loadingUsers} />}
            {matrix.data !== undefined && <MatrixTable matrix={matrix.data} />}
          </>
        )}

        {tab === 'notifications' && <RoleNotificationSettings />}

      </Tabs>

      {adding && <AddUserModal onClose={() => setAdding(false)} />}
      {editing !== null && <EditUserModal user={editing} onClose={() => setEditing(null)} />}
      {revoking !== null && (
        <ConfirmSheet
          title={strings.users.revokeTitle}
          statement={strings.users.revokeStatement(revoking.name)}
          confirmLabel={strings.users.revokeConfirm}
          cancelLabel={strings.users.revokeCancel}
          closeLabel={strings.sheet.close}
          busy={revoke.isPending}
          onCancel={() => setRevoking(null)}
          onConfirm={() =>
            revoke.mutate(revoking.id, {
              onSuccess: () => {
                toast(`${strings.users.revokedToast} ${revoking.name}. ${strings.users.revokedDetail}`);
                setRevoking(null);
              },
              onError: (error) => {
                toast(failureWords(error));
                setRevoking(null);
              },
            })
          }
        />
      )}
      {settingPasswordFor !== null && (
        <SetPasswordModal user={settingPasswordFor} onClose={() => setSettingPasswordFor(null)} />
      )}
    </div>
  );
}

// The sign in location moved to the Settings screen. Section 2.3 of the
// system test handover.
const TAB_KEYS = ['team', 'revoked', 'matrix', 'notifications'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * The columns of the team list, as it comes. The name is who the row is and
 * the last column holds what is done to them, Revoke, Edit and Set password,
 * so neither can be hidden; both can be moved.
 *
 * Added, the day the account was made, is offered and left out until it is
 * ticked. The request of 8 October 2026 (second).
 */
const TEAM_COLUMNS = [
  { key: 'name', label: strings.users.name, locked: true },
  { key: 'username', label: strings.users.username },
  { key: 'role', label: strings.users.role },
  { key: 'department', label: strings.users.department },
  { key: 'jobTitle', label: strings.users.jobTitle },
  { key: 'access', label: strings.users.access, locked: true },
  { key: 'added', label: strings.table.columns.added, optional: true },
] as const;
type TeamColumn = (typeof TEAM_COLUMNS)[number]['key'];

/** The same table for both people tabs, so the two cannot drift apart. */
function TeamTable({
  rows,
  shown,
  customize,
}: {
  rows: React.ReactNode[];
  /** The keys of the columns to draw, in the reader's order. */
  shown: readonly string[];
  customize: TableCustomize;
}) {
  return (
    <>
      <div className={styles.tools}>
        <CustomizeColumns columns={TEAM_COLUMNS} customize={customize} />
      </div>
      <div className={styles.wrap}>
        <table className={styles.team}>
          <thead>
            <tr>
              {shown.map((key) => (
                <th key={key}>{TEAM_COLUMNS.find((column) => column.key === key)?.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </>
  );
}

function TeamRow({
  shown,
  user,
  isSelf,
  mayRevoke,
  busy,
  onOpen,
  onRevoke,
  onRestore,
  onSetPassword,
  onEdit,
}: {
  /** The keys of the columns to draw, in the reader's order. */
  shown: readonly string[];
  user: User;
  isSelf: boolean;
  mayRevoke: boolean;
  busy: boolean;
  onOpen: () => void;
  onRevoke: () => void;
  onRestore: () => void;
  onSetPassword: () => void;
  onEdit: () => void;
}) {
  // Every cell by its column, so the row draws the ones the reader chose in their order.
  const cells: Record<TeamColumn, React.ReactNode> = {
    name: (
      /*
        The name opens the person's work record. A button rather than a
        clickable row: the last cell holds Revoke, and a row that navigates
        would swallow it or fire both.
      */
      <td key="name" data-label={strings.users.name}>
        <button className={styles.openUser} onClick={onOpen}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(user.name)}
          </span>
          {user.name}
        </button>
        {isSelf && <span className={styles.you}> {strings.users.you}</span>}
        {/* Somebody with exceptions to their role, set on their own record. */}
        {user.hasAccessOverrides && <span className={styles.pill}>{strings.access.pill}</span>}
      </td>
    ),
    username: (
      <td key="username" data-label={strings.users.username} className={styles.mono}>
        {user.username}
      </td>
    ),
    role: (
      <td key="role" data-label={strings.users.role}>
        {ROLE_LABEL[user.role]}
      </td>
    ),
    department: (
      <td key="department" data-label={strings.users.department}>
        {DEPARTMENT_LABEL[user.department]}
      </td>
    ),
    jobTitle: (
      <td key="jobTitle" data-label={strings.users.jobTitle} className={styles.dim}>
        {user.title}
        {/*
          "Supervisor · Ceramic" beside the job title. Section 11.2 of the
          request of 20 September 2026: the flag is labelled with the department
          it supervises, so a manager reading the table knows who can pass a case
          without opening every record.
        */}
        {user.isSupervisor && (
          <span className={styles.badge}>
            {strings.users.supervisorOf} · {DEPARTMENT_LABEL[user.department]}
          </span>
        )}
      </td>
    ),
    added: (
      <td key="added" data-label={strings.table.columns.added} className={styles.mono}>
        {formatDay(user.createdAt)}
      </td>
    ),
    access: (
      <td key="access" data-label={strings.users.access} className={styles.accessCell}>
        {user.disabled ? (
          <>
            <span className={styles.badge}>{strings.users.revoked}</span>
            <button className={styles.mini} disabled={busy} onClick={onRestore}>
              {strings.users.restore}
            </button>
          </>
        ) : !mayRevoke ? (
          // Section 7.10 item 8. Offering a button that always 400s would be a
          // worse screen than not offering it.
          <span className={styles.dim} title={strings.users.cannotRevokeSelf}>
            {strings.users.active}
          </span>
        ) : (
          <button
            className={`${styles.mini} ${styles.revoke}`}
            disabled={busy}
            onClick={onRevoke}
          >
            {strings.users.revoke}
          </button>
        )}
        {/*
          Not on your own row: your own password needs your current one, so it
          goes through Change password rather than past that check.
        */}
        {/* Name, role, department and supervisor. Request of 4 October 2026, section 3. */}
        {!user.disabled && (
          <button
            className={`${styles.mini} ${styles.gap}`}
            disabled={busy}
            aria-label={`${strings.users.editFor} ${user.name}`}
            onClick={onEdit}
          >
            {strings.users.edit}
          </button>
        )}
        {!isSelf && (
          <button
            className={`${styles.mini} ${styles.gap}`}
            disabled={busy}
            aria-label={`${strings.users.setPasswordFor} ${user.name}`}
            onClick={onSetPassword}
          >
            {strings.users.setPassword}
          </button>
        )}
      </td>
    ),
  };

  return (
    <tr className={user.disabled ? styles.off : undefined}>
      {shown.map((key) => cells[key as TeamColumn])}
    </tr>
  );
}

/**
 * The matrix is held locally while editing so a tick is instant on a phone on
 * the floor, and saved on every change. A rejected save reverts to the server's
 * copy rather than leaving the screen showing a grant that does not exist.
 *
 * A tick is asked about before it is applied, the request of 5 October 2026
 * (third), section 2: the box changes only once the question is answered, so
 * Cancel leaves it as it was and nothing is sent. A locked box cannot be
 * pressed and so asks nothing.
 */
function MatrixTable({ matrix }: { matrix: AccessMatrix }) {
  const [draft, setDraft] = useState(matrix);
  const save = useSaveAccessMatrix();
  const session = useSession();
  const ask = useConfirmAction();

  useEffect(() => {
    setDraft(matrix);
  }, [matrix]);

  function toggle(kind: 'view' | 'permission', key: string, role: Role, on: boolean) {
    const apply = <Row extends { key: string; roles: Role[] }>(rows: Row[]): Row[] =>
      rows.map((row) =>
        row.key !== key
          ? row
          : { ...row, roles: on ? [...row.roles, role] : row.roles.filter((r) => r !== role) },
      );

    const next: AccessMatrix = {
      ...draft,
      ...(kind === 'view'
        ? { views: apply(draft.views) }
        : { permissions: apply(draft.permissions) }),
    };

    const label =
      (kind === 'view'
        ? next.views.find((v) => v.key === key)?.label
        : next.permissions.find((p) => p.key === key)?.label) ?? key;

    ask(strings.confirm.grantAccess(ROLE_LABEL[role], label, on), () => {
      setDraft(next);
      return save.mutateAsync(next).then(
        () => {
          toast(
            `${on ? strings.users.granted : strings.users.revokedFrom} ${ROLE_LABEL[role]}: ${label}`,
          );
          // The signed in manager may have just changed their own menu.
          void session.refetch();
        },
        (error: unknown) => {
          setDraft(matrix);
          // Said by the confirmation, in the server's words.
          throw error;
        },
      );
    });
  }

  // What each role holds that will not work as it stands, as the boxes are now.
  const gaps = ROLES.flatMap((role) =>
    accessGaps(
      role,
      draft.views.filter((view) => view.roles.includes(role)).map((view) => view.key),
      draft.permissions.filter((permission) => permission.roles.includes(role)).map((permission) => permission.key),
    ).map((sentence) => ({ who: ROLE_LABEL[role], sentence })),
  );

  return (
    <>
      <div className={styles.matrixScroll}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th className={styles.what}>{strings.users.permission}</th>
              {ROLES.map((role) => (
                <th key={role}>{ROLE_LABEL[role]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className={styles.group} colSpan={ROLES.length + 1} scope="colgroup">
                {strings.users.screens}
              </th>
            </tr>
            {draft.views.map((view) => (
              <MatrixRow
                key={view.key}
                label={view.label}
                rowKey={view.key}
                roles={view.roles}
                /*
                 * The one lock there is. The Accounts column had every screen
                 * but three locked, by the request of 5 October 2026 (third),
                 * section 10; the request of 6 October 2026 (fourth) took that
                 * away, and its boxes are ticked and asked about like any
                 * other role's.
                 */
                lockedFor={(role) =>
                  isLockedView(view.key) && role === 'management' ? strings.users.lockedHint : null
                }
                onToggle={(role, on) => toggle('view', view.key, role, on)}
              />
            ))}

            <tr>
              <th className={styles.group} colSpan={ROLES.length + 1} scope="colgroup">
                {strings.users.actions}
              </th>
            </tr>
            {draft.permissions.map((permission) => (
              <MatrixRow
                key={permission.key}
                label={permission.label}
                rowKey={permission.key}
                roles={permission.roles}
                lockedFor={(role) =>
                  isLockedPermission(permission.key) && role === 'management'
                    ? strings.users.lockedHint
                    : null
                }
                onToggle={(role, on) => toggle('permission', permission.key, role, on)}
              />
            ))}
          </tbody>
        </table>
      </div>
      {/* The lab's request of 11 October 2026: said here, where the boxes are ticked. */}
      <AccessGaps gaps={gaps} />
    </>
  );
}

function MatrixRow({
  label,
  rowKey,
  roles,
  lockedFor,
  onToggle,
}: {
  label: string;
  rowKey: string;
  roles: Role[];
  /** Why this role's box cannot be changed, in words, or null when it can. */
  lockedFor: (role: Role) => string | null;
  onToggle: (role: Role, on: boolean) => void;
}) {
  return (
    <tr>
      <th scope="row" className={styles.what}>
        {label}
      </th>
      {ROLES.map((role) => {
        const hint = lockedFor(role);
        const locked = hint !== null;
        return (
          <td key={role}>
            <label className={styles.tick}>
              <input
                type="checkbox"
                checked={roles.includes(role)}
                disabled={locked}
                aria-label={`${label}: ${ROLE_LABEL[role]}${locked ? ` (${strings.users.locked})` : ''}`}
                // The reason, read out after the name and shown on a pointer.
                title={hint ?? undefined}
                data-row={rowKey}
                onChange={(event) => onToggle(role, event.target.checked)}
              />
              {locked && (
                <span className={styles.lock} title={hint} aria-hidden="true">
                  <LockIcon />
                </span>
              )}
            </label>
          </td>
        );
      })}
    </tr>
  );
}
